/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo } from 'react';

import { css, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';

const textColor = token('color.text');
const initialsAvatarColor = token('color.background.neutral');

/** Shown when name is empty or whitespace-only (External Person) */
const DEFAULT_INITIALS = 'EP';

const initialsCss = css({
	width: '100%',
	height: '100%',
});

interface InitialsProps {
	name: string;
}

/**
 * Renders the user's initials
 * @example For user Daniel Porter => "DP"
 **/
export const Initials: ({ name }: InitialsProps) => JSX.Element = ({ name }: InitialsProps) => {
	const initials = useMemo(() => getInitials(name), [name]);

	return (
		<svg css={initialsCss} width="20" height="20" viewBox="0 0 20 20">
			<circle cx="10" cy="10" r="10" fill={initialsAvatarColor} />
			<text
				x="50%"
				y="55%"
				fontFamily="SF Pro Text, sans-serif"
				dominantBaseline="middle"
				textAnchor="middle"
				fontSize="11"
				fontWeight="400"
				fill={textColor}
			>
				{initials}
			</text>
		</svg>
	);
};

function getInitials(name: string): string {
	const trimmed = name.trim();
	if (!trimmed) {
		return DEFAULT_INITIALS;
	}
	const names = trimmed.split(/\s+/).filter(Boolean);
	const lastName = names.length > 1 ? names[names.length - 1] : '';
	const firstInitial = names[0][0].toUpperCase();
	const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
	return firstInitial + lastInitial;
}
